import moment from 'moment'
import { Entity } from '@core/entities/entity'
import { plainToInstance, Type } from 'class-transformer'
import { IAppNotification } from '@core/entities/notifications/IAppNotification'
import {
  AppNotificationAction,
  AppNotificationReferenceObject,
  AppNotificationTargetResult,
  appNotificationTargetResultClassMap,
} from '@core/entities/notifications/types'
import { User } from '@core/entities/user'
import { localizedStrings } from '@core/strings'
import { ProfileOrganisation } from '../profile'

export class AppNotification<
  T extends AppNotificationReferenceObject = AppNotificationReferenceObject,
> extends Entity<IAppNotification<T>> {
  id: number
  userId: number
  isRead: boolean
  createdOn: string

  action: AppNotificationAction
  referenceObjectType: T

  @Type(({ object }) => {
    const notification = object as IAppNotification<T>
    return appNotificationTargetResultClassMap[notification.referenceObjectType]
  })
  targetResult: AppNotificationTargetResult<T>

  @Type(() => User)
  actor?: User

  public static new(payload: unknown): AppNotification {
    const entity = plainToInstance(AppNotification, payload)
    return entity as AppNotification
  }

  /**
   * This method is used to narrow down the type of `targetResult`.
   *
   * @example
   * if (notification.isTargetResultOfType('PERMIT')) {
   *   // Within this block, TS knows that `notification.targetResult` is of type `PermitTargetResult`
   *   notification.targetResult.shortUUID;
   * }
   */
  public isTargetResultOfType<U extends AppNotificationReferenceObject>(
    referenceObjectType: U,
  ): this is AppNotification<U> {
    // Cast to `AppNotificationReferenceObject` is needed to allow usage of
    // method in class, i.e. this.isTargetResultOfType('PERMIT')
    return (
      (this.referenceObjectType as AppNotificationReferenceObject) ===
      referenceObjectType
    )
  }

  public getIsTargetResultKnown() {
    return !!appNotificationTargetResultClassMap[this.referenceObjectType]
  }

  public getTitle() {
    // Competency notification subjects are spec'd by Notion §9 (and the BE
    // i18n keys) without any actor postfix — the message reads as a direct
    // instruction to the recipient. Suppress the "by {actor}" suffix here
    // even when the BE happens to attach one (template activations carry
    // the admin who clicked Activate).
    const suppressActor = this.referenceObjectType === 'COMPETENCY'
    const userPostfix =
      this.actor && !suppressActor
        ? ` ${localizedStrings.by.toLowerCase()} ${this.actor.getFullName()}`
        : ''

    return `${getTitleActionText(this)}${userPostfix}`
  }

  public getDescription({
    organisations,
  }: {
    organisations: ProfileOrganisation[]
  }) {
    return getDescriptionText(this, organisations)
  }

  public getFormattedCreatedOn() {
    return moment(this.createdOn).format('DD MMM YYYY HH:mm')
  }
}

const getTitleActionText = (notification: AppNotification): string => {
  switch (notification.action) {
    case 'SENT_TO_PERMIT_HOLDER':
      return localizedStrings.permitAssignedToYou
    case 'REQUESTED':
      return localizedStrings.permitRequested
    case 'ISSUED':
      return localizedStrings.permitApproved
    case 'OPENED':
      return localizedStrings.permitIsNowOpen
    case 'EXTENSION_REQUESTED':
      return localizedStrings.permitExtensionRequested
    case 'EXTENSION_APPROVED':
      return localizedStrings.permitExtensionApprovedNotifications
    case 'EXTENSION_REJECTED':
      return localizedStrings.permitExtensionRejectedNotifications
    case 'DISCARDED':
      return localizedStrings.permitDiscarded
    case 'AWAITING_FOR_FINAL_SIGN_OFF':
      return localizedStrings.finalSignOffRequested
    case 'CLOSED_ADMIN':
    case 'CLOSED_USER':
      return localizedStrings.permitIsNowClosed
    case 'CANCELLED':
      return localizedStrings.permitCancelledNotifications
    case 'COUNTDOWN_FINISHED':
    case 'REMIND_POST_PERMIT_CHECKS':
      return localizedStrings.permitRequiresPostPermitChecks
    case 'EXPIRED':
      return localizedStrings.permitHasExpired
    case 'ABOUT_TO_EXPIRE': {
      const endsOn =
        notification.isTargetResultOfType('PERMIT') &&
        notification.targetResult.endsOn
      return localizedStrings.permitExpiresAt(endsOn, notification.createdOn)
    }
    case 'CHANGES_REQUESTED':
      return localizedStrings.changesRequestedNotifications
    case 'CHANGED_ADDRESSED':
      return localizedStrings.changesAddressedNotifications
    case 'PARTICIPANT_ADDED':
      if (notification.isTargetResultOfType('PERMIT')) {
        return localizedStrings.youHaveBeenAddedToAPermit
      } else if (notification.isTargetResultOfType('CHECKLIST')) {
        return notification.targetResult.isInspection
          ? localizedStrings.youHaveBeenAddedToAnInspection
          : localizedStrings.youHaveBeenAddedToAChecklist
      }
      return ''
    case 'PARTICIPANT_ADDED_FOR_ACCEPTANCE':
      return localizedStrings.youHaveBeenAddedToAPermit
    case 'BRIEFING_ACCEPTED':
      return localizedStrings.briefingAccepted
    case 'BRIEFING_DECLINED':
      return localizedStrings.briefingDeclined
    case 'TRANSFER_INITIATED':
      return localizedStrings.permitTransferInitiatedNotifications
    case 'TRANSFER_REQUESTED':
      return localizedStrings.permitTransferRequestedNotifications
    case 'TRANSFER_ACCEPTED':
      return localizedStrings.permitTransferApprovedNotifications
    case 'TRANSFER_REJECTED':
      return localizedStrings.permitTransferRejectedNotifications
    case 'TRANSFER_CANCELLED':
      return localizedStrings.permitTransferCancelledNotifications
    case 'SUSPENDED':
      return localizedStrings.permitSuspendedNotifications
    case 'RESUMED':
      return localizedStrings.permitResumedNotifications
    case 'SUSPENSION_RESUME_REQUESTED':
      return localizedStrings.requestToResumePermit
    case 'SUSPENSION_RESUME_REQUEST_APPROVED':
      return localizedStrings.resumeRequestApproved
    case 'SUSPENSION_RESUME_REQUEST_DECLINED':
      return localizedStrings.requestToResumePermitRejected
    case 'REGISTER_ITEM_CREATED':
      return localizedStrings.registerItemCreated
    case 'REGISTER_ITEM_UPDATED':
      return localizedStrings.registerItemUpdated
    case 'REGISTER_ITEM_SCHEDULED_INSPECTION_CREATED':
      return localizedStrings.inspectionCreatedNotification
    case 'REGISTER_ITEM_SCHEDULED_INSPECTION_UPDATED':
      return localizedStrings.inspectionUpdatedNotification
    case 'REGISTER_ITEM_INSPECTION_OVERDUE':
      return localizedStrings.inspectionOverdueNotification
    case 'COMMENT_ADDED':
      return localizedStrings.newComment
    case 'REJECTED':
      return localizedStrings.permitRejectedNotifications
    case 'TASK_ASSIGNED':
      return localizedStrings.taskAssignedNotifications
    case 'TASK_UPDATED':
      return localizedStrings.taskUpdatedNotifications
    case 'TASK_CANCELLED':
      return localizedStrings.taskCancelledNotifications
    case 'TASK_COMPLETED':
      return localizedStrings.taskCompletedNotifications
    case 'TASK_DUE_DATE_REMINDER_SENT':
      return localizedStrings.taskDueDateReminderSentNotifications
    case 'COMPANY_ONBOARDING_FORM_SUBMITTED_REVIEWER':
      return localizedStrings.companyOnboardingFormSubmitted
    case 'COMPANY_COMPLIANCE_DOCUMENT_UPLOADED_REVIEWER':
      return localizedStrings.complianceRequirementUploadedForReview
    case 'COMPANY_COMPLIANCE_DOCUMENT_REJECTED':
      return localizedStrings.complianceRequirementRejectedNotification
    case 'COMPANY_COMPLIANCE_COMPANY_ACTIVATED':
      return localizedStrings.companyIsNowActive
    case 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRING_28_DAYS':
      return localizedStrings.complianceRequirementExpiringIn(28)
    case 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRING_7_DAYS':
      return localizedStrings.complianceRequirementExpiringIn(7)
    case 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED_COMPANY_ADMIN':
    case 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED_REVIEWER':
      return localizedStrings.companyComplianceRequirementExpired
    case 'CHECKLIST_COMPLETED':
      if (
        notification.isTargetResultOfType('CHECKLIST') &&
        notification.targetResult.isInspection
      ) {
        return localizedStrings.inspectionCompleted
      } else {
        return localizedStrings.checklistCompleted
      }
    case 'OBSERVATION_RESPONSES_READY':
      return localizedStrings.observationReadyForTriage
    case 'OBSERVATION_ASSIGNED':
      return localizedStrings.observationAssignedNotification
    case 'OBSERVATION_CLOSED':
      return localizedStrings.observationClosedNotification
    case 'OBSERVATION_REJECTED':
      return localizedStrings.observationRejectedNotification
    case 'COMPETENCY_TEMPLATE_ACTIVATED':
      if (!notification.isTargetResultOfType('COMPETENCY')) return ''
      return localizedStrings.competencyNotificationActivated(
        notification.targetResult.competencyName ?? '',
      )
    case 'COMPETENCY_EXPIRING_30_DAYS':
      if (!notification.isTargetResultOfType('COMPETENCY')) return ''
      return localizedStrings.competencyNotificationExpiring30Days(
        notification.targetResult.competencyName ?? '',
        notification.targetResult.expiresOn
          ? moment(notification.targetResult.expiresOn).format('DD MMM YYYY')
          : '',
      )
    case 'COMPETENCY_EXPIRING_7_DAYS':
      if (!notification.isTargetResultOfType('COMPETENCY')) return ''
      return localizedStrings.competencyNotificationExpiring7Days(
        notification.targetResult.competencyName ?? '',
      )
    case 'COMPETENCY_EXPIRED':
      if (!notification.isTargetResultOfType('COMPETENCY')) return ''
      return localizedStrings.competencyNotificationExpired(
        notification.targetResult.competencyName ?? '',
      )
    case 'COMPETENCY_TEMPLATE_VERSION_PUBLISHED':
      if (!notification.isTargetResultOfType('COMPETENCY')) return ''
      return localizedStrings.competencyNotificationVersionPublished(
        notification.targetResult.competencyName ?? '',
        notification.targetResult.expiresOn
          ? moment(notification.targetResult.expiresOn).format('DD MMM YYYY')
          : '',
      )
    default:
      API.errorTracking.notify(
        new Error(`Unknown notification action: ${notification.action}`),
      )
      // Convert the string to lowercase and replace underscores with spaces
      const lowerCased = notification.action.toLowerCase().replace(/_/g, ' ')

      // Capitalize only the first letter and return the transformed string
      return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1)
  }
}

const getDescriptionText = (
  notification: AppNotification,
  organisations: ProfileOrganisation[],
): string => {
  if (
    notification.isTargetResultOfType('PERMIT') ||
    notification.isTargetResultOfType('CHECKLIST')
  ) {
    // The template stub is optional on the payload, so fall back to the short uuid alone
    const lines = [
      [
        notification.targetResult.template?.name,
        notification.targetResult.shortUUID,
      ]
        .filter(Boolean)
        .join(' - '),
    ]
    if (notification.targetResult.summary) {
      const summary = notification.targetResult.summary
      const maxChars = 50
      if (summary.length > maxChars) {
        lines.push(`${summary.substring(0, maxChars).trim()}...`)
      } else {
        lines.push(summary)
      }
    }
    return lines.join('\n')
  } else if (notification.isTargetResultOfType('TASK')) {
    return [
      notification.targetResult.template.name,
      notification.targetResult.summary,
    ]
      .filter(Boolean)
      .join('\n')
  } else if (notification.isTargetResultOfType('PROJECT')) {
    const orgName = organisations.find(
      (org) => org.id === notification.targetResult.organisationId,
    )?.name
    return `${notification.targetResult.name} - ${orgName}`
  } else if (notification.isTargetResultOfType('COMPANY_ONBOARDING')) {
    return (
      notification.targetResult.getCompanyName() ||
      localizedStrings.companyOnboardingFormSubmitted
    )
  } else if (notification.isTargetResultOfType('COMPANY_COMPLIANCE')) {
    const { companyName, requirementTitle } = notification.targetResult
    return [companyName, requirementTitle].filter(Boolean).join(' — ')
  } else if (notification.isTargetResultOfType('OBSERVATION')) {
    return notification.targetResult.getTitleText()
  } else if (notification.isTargetResultOfType('COMPETENCY')) {
    // The competency name lives in the title (per Notion §9 subjects), so the
    // description is intentionally empty to avoid duplicating the same name.
    return ''
  } else if (notification.isTargetResultOfType('REGISTER_ITEM')) {
    const registerItem = notification.targetResult
    // Register name first, then item description — matches the permit rows' template-then-summary order
    return [registerItem.registerName, registerItem.getDescriptionText()]
      .filter(Boolean)
      .join('\n')
  }

  return notification.referenceObjectType
}
